import { useState, useRef, useEffect, useCallback } from 'react'

// Hook partage du flow de creation de clip (utilise par TrimOverlay ;
// InlineTrimBar et CreateClipModal restent dans le repo mais ne sont plus montes).
//
// Chainage (2 appels backend) :
// 1) POST /api/matches/{matchId}/moments/   { timecode_start, timecode_end, moment_type, note }
// 2) POST /api/moments/{id}/extract-clip/    { start_time, end_time }
// 3) GET  /api/moments/{id}/clip-status/     (poll 2s, timeout 180s)

const API = import.meta.env.VITE_API_URL || 'https://backend-pued.onrender.com/api'

function authHeaders() {
  const token = localStorage.getItem('insightball_token')
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
}

function extractError(data) {
  const d = data?.detail
  if (Array.isArray(d)) return d.map(e => e?.msg).filter(Boolean).join(' · ')
  if (typeof d === 'string') return d
  if (d && typeof d === 'object') return d.message || null
  return null
}

export default function useCreateClip() {
  const [isGenerating, setIsGenerating] = useState(false)
  const [pollStatus, setPollStatus] = useState(null)   // null|'processing'|'ready'|'failed'
  const [error, setError] = useState(null)
  const pollTimerRef = useRef(null)
  const mountedRef = useRef(true)

  const stopPolling = () => {
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null }
  }

  useEffect(() => () => { mountedRef.current = false; stopPolling() }, [])

  const pollUntilDone = (momentId) => new Promise((resolve) => {
    const deadline = Date.now() + 180000
    stopPolling()
    pollTimerRef.current = setInterval(async () => {
      if (!mountedRef.current) { stopPolling(); return }
      if (Date.now() > deadline) {
        stopPolling()
        resolve({ status: 'failed', error: 'Delai depasse (180s). Reessaie.' })
        return
      }
      try {
        const r = await fetch(`${API}/moments/${momentId}/clip-status/`, { headers: authHeaders() })
        if (!r.ok) return
        const data = await r.json()
        if (data.status === 'ready' || data.status === 'failed') { stopPolling(); resolve(data) }
      } catch { /* tick suivant */ }
    }, 2000)
  })

  const reset = useCallback(() => {
    stopPolling()
    setError(null)
    setPollStatus(null)
    setIsGenerating(false)
  }, [])

  // Retourne { status: 'ready'|'failed'|'aborted', moment?, error? }
  // half: 1|2 pour les matchs decoupes MT1/MT2 (timecodes relatifs a la mi-temps)
  const generate = useCallback(async ({ matchId, inTime, outTime, tag, note, half }) => {
    setError(null)
    setIsGenerating(true)
    setPollStatus('processing')
    try {
      const createRes = await fetch(`${API}/matches/${matchId}/moments/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          timecode_start: inTime,
          timecode_end: outTime,
          moment_type: tag || 'tactical_note',
          note: (note || '').trim() || null,
          ...(half === 1 || half === 2 ? { half } : {}),
        }),
      })
      if (!createRes.ok) {
        const data = await createRes.json().catch(() => ({}))
        throw new Error(extractError(data) || 'La creation du moment a echoue.')
      }
      const moment = await createRes.json()

      const extractRes = await fetch(`${API}/moments/${moment.id}/extract-clip/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ start_time: inTime, end_time: outTime }),
      })
      if (!extractRes.ok) {
        const data = await extractRes.json().catch(() => ({}))
        throw new Error(extractError(data) || 'Le lancement de la generation a echoue.')
      }

      const result = await pollUntilDone(moment.id)
      if (!mountedRef.current) return { status: 'aborted' }
      if (result.status === 'ready') {
        setPollStatus('ready')
        setIsGenerating(false)
        return { status: 'ready', moment: { ...moment, ...result } }
      }
      const msg = result.error || 'La generation du clip a echoue.'
      setPollStatus('failed')
      setIsGenerating(false)
      setError(msg)
      return { status: 'failed', error: msg }
    } catch (e) {
      if (mountedRef.current) {
        setPollStatus('failed')
        setIsGenerating(false)
        setError(e.message)
      }
      return { status: 'failed', error: e.message }
    }
  }, [])

  return { generate, isGenerating, pollStatus, error, reset }
}
